import { useContext, useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import styles from './styles.module.css'
import { ToastNotificationContext } from '../../../global/context/toast-context/ToastNotificationContext'
import { fetchData } from '../../../global/utils/fetch'
import PrimaryButton from '../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../global/components/buttons/secondary-button/SecondaryButton'
import { formatDate } from '../../../global/utils/date/mm-dd-yyyy'
import {
  DEFAULT_USER,
  OPERATING_STATUSES,
  OPERATING_STATUSES_ARRAY,
  VAN_DOWN_MODAL_MODES,
  VAN_STATUS_SOURCES,
} from '../../../global/constants/van'
import { UserContext } from '../../../base/components/base-container/BaseContainer'

interface IVanDownModalProps {
  isOpen: boolean
  onClose: () => void
  vanObjectId: string | null
  vanName: string
  currentStatus: string
  currentIssue: string
  inOpDate: Date | null
  vanFixPendingApproval: boolean
  refetchTable: () => void
}

type ModalMode = typeof VAN_DOWN_MODAL_MODES[keyof typeof VAN_DOWN_MODAL_MODES]

interface IResolveIssueResult {
  success: boolean
  remainingUnresolvedCount: number
  isLastIssue: boolean
}

interface IUpdateStatusResult {
  van: any
  unresolvedIssuesCount?: number
  unresolvedIssues?: any[]
}

export default function VanDownModal(props: IVanDownModalProps) {
  const {
    isOpen,
    onClose,
    vanObjectId,
    vanName,
    currentStatus,
    inOpDate,
    vanFixPendingApproval,
    refetchTable,
  } = props
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)
  const { user } = useContext(UserContext)

  // Modal mode
  const [mode, setMode] = useState<ModalMode>(VAN_DOWN_MODAL_MODES.RESOLVE_ISSUES)

  // Issue resolution state
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null)
  const [resolvedNote, setResolvedNote] = useState('')
  const [isResolvingIssue, setIsResolvingIssue] = useState(false)

  // Status change state
  const [operatingStatus, setOperatingStatus] = useState(OPERATING_STATUSES.ACTIVE)
  const [statusDate, setStatusDate] = useState(
    new Date().toISOString().split('T')[0],
  )
  const [forceStatusChange, setForceStatusChange] = useState(false)
  const [isChangingStatus, setIsChangingStatus] = useState(false)

  // History data
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const [allIssues, setAllIssues] = useState<any[]>([])
  const [techFixNote, setTechFixNote] = useState('')
  const [techName, setTechName] = useState('')

  useEffect(() => {
    async function fetchHistory() {
      if (isOpen && vanObjectId) {
        setIsLoadingHistory(true)
        try {
          const response = await fetchData<Array<{ source: string; fixNote?: string; reportedBy?: string; objectId?: string; resolved?: boolean }>>({
            url: `${process.env.REACT_APP_ORIGIN}/vans/${vanObjectId}/operating-status-history`,
            method: 'GET',
          })

          // Find the most recent tech fix request
          const fixRequest = response.find(
            (h) => h.source === VAN_STATUS_SOURCES.TECH_FIX_REQUEST,
          )
          if (fixRequest) {
            setTechFixNote(fixRequest.fixNote || '')
            setTechName(fixRequest.reportedBy || DEFAULT_USER)
          }

          // Get all inoperative entries (both resolved and unresolved, excluding tech fix requests)
          const issues = response.filter(
            (h: any) =>
              h.operatingStatus === OPERATING_STATUSES.INOPERATIVE &&
              h.source !== VAN_STATUS_SOURCES.TECH_FIX_REQUEST,
          )

          // Sort by created date (oldest first)
          issues.sort(
            (a: any, b: any) =>
              new Date(a.created).getTime() - new Date(b.created).getTime(),
          )

          setAllIssues(issues)

          // Auto-select first unresolved issue
          const firstUnresolved = issues.find((i: any) => !i.resolved)
          if (firstUnresolved) {
            setSelectedIssueId(firstUnresolved.objectId || null)
          }
        } catch (error) {
          console.error('Error fetching history:', error)
        } finally {
          setIsLoadingHistory(false)
        }
      }
    }

    if (isOpen) {
      // Reset state
      setMode(VAN_DOWN_MODAL_MODES.RESOLVE_ISSUES)
      setSelectedIssueId(null)
      setResolvedNote('')
      setOperatingStatus(OPERATING_STATUSES.ACTIVE)
      setStatusDate(new Date().toISOString().split('T')[0])
      setForceStatusChange(false)
      setTechFixNote('')
      setTechName('')
      setAllIssues([])

      fetchHistory()
    }
  }, [isOpen, vanObjectId])

  async function handleResolveIssue() {
    if (!selectedIssueId || !vanObjectId) return

    if (!resolvedNote.trim()) {
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Resolution note is required',
        severity: 'error',
      })
      return
    }

    setIsResolvingIssue(true)
    try {
      const result = await fetchData<IResolveIssueResult>({
        url: `${process.env.REACT_APP_ORIGIN}/vans/issues/${selectedIssueId}/resolve`,
        method: 'PUT',
        body: {
          resolvedNote,
          resolvedBy: user?.name || user?.email || DEFAULT_USER,
        },
      })

      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: `Issue resolved successfully`,
        severity: 'success',
      })

      // Update the allIssues array to mark this issue as resolved
      const updatedIssues = allIssues.map((issue) =>
        issue.objectId === selectedIssueId
          ? { ...issue, resolved: true, resolvedBy: user?.name || user?.email || DEFAULT_USER, resolvedDate: new Date(), resolvedNote }
          : issue
      )
      setAllIssues(updatedIssues)

      // Clear resolution note
      setResolvedNote('')

      // Check if this was the last issue
      if (result.isLastIssue) {
        // Show status change prompt
        setMode(VAN_DOWN_MODAL_MODES.CHANGE_STATUS)
        setSelectedIssueId(null)
      } else {
        // Auto-select next unresolved issue
        const nextUnresolved = updatedIssues.find((i: any) => !i.resolved && i.objectId !== selectedIssueId)
        if (nextUnresolved) {
          setSelectedIssueId(nextUnresolved.objectId)
        }
      }
    } catch (error) {
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Error resolving issue',
        severity: 'error',
      })
    } finally {
      setIsResolvingIssue(false)
    }
  }

  async function handleStatusChange() {
    if (!vanObjectId) return

    setIsChangingStatus(true)
    try {
      const result = await fetchData<IUpdateStatusResult>({
        url: `${process.env.REACT_APP_ORIGIN}/vans/${vanObjectId}/operating-status`,
        method: 'PUT',
        body: {
          operatingStatus,
          operatingStatusStartDate: new Date(statusDate),
          note: '',
          forceUpdate: forceStatusChange,
          changedBy: user?.name || user?.email || DEFAULT_USER,
        },
      })

      // Check if there are unresolved issues
      if (
        result.unresolvedIssuesCount &&
        result.unresolvedIssuesCount > 0 &&
        !forceStatusChange
      ) {
        // Show warning
        setToastStatus({
          ...toastStatus,
          isOpen: true,
          message: `Warning: ${result.unresolvedIssuesCount} unresolved issue(s) still exist. Click "Force Update" to proceed anyway.`,
          severity: 'warning',
        })
        setForceStatusChange(true)
        return
      }

      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: `Van status updated to ${operatingStatus}`,
        severity: 'success',
      })

      refetchTable()
      onClose()
    } catch (error) {
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Error updating van status',
        severity: 'error',
      })
    } finally {
      setIsChangingStatus(false)
    }
  }

  function renderResolveIssuesMode() {
    if (isLoadingHistory) {
      return <p>Loading issues...</p>
    }

    const unresolvedIssues = allIssues.filter((i) => !i.resolved)

    if (unresolvedIssues.length === 0) {
      // No unresolved issues, allow direct status change
      return (
        <div>
          <div
            style={{
              backgroundColor: 'var(--status-success-bg)',
              padding: '16px',
              borderRadius: '4px',
              marginBottom: '16px',
            }}
          >
            <p style={{ margin: 0, color: 'var(--status-success-text)', fontWeight: 'bold' }}>
              ✅ No unresolved issues found
            </p>
            <p style={{ margin: '8px 0 0 0', color: 'var(--status-success-text)' }}>
              You can change the van status directly.
            </p>
          </div>
          <PrimaryButton
            buttonName='Change Van Status'
            onClick={() => setMode(VAN_DOWN_MODAL_MODES.CHANGE_STATUS)}
          />
        </div>
      )
    }

    return (
      <div>
        {/* Tech Fix Request Banner */}
        {vanFixPendingApproval && techFixNote && (
          <div
            style={{
              backgroundColor: 'var(--status-warning-bg)',
              border: '2px solid var(--color-gold)',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '16px',
            }}
          >
            <h4 style={{ color: 'var(--status-tech-unavailable-accent)', margin: '0 0 8px 0' }}>
              Technician Fix Request
            </h4>
            <p style={{ margin: '4px 0' }}>
              <strong>Reported by:</strong> {techName}
            </p>
            <p style={{ margin: '8px 0 0 0', fontStyle: 'italic' }}>
              "{techFixNote}"
            </p>
          </div>
        )}

        {/* Unresolved Issues List */}
        <div style={{ marginBottom: '16px' }}>
          <p style={{ margin: '0 0 12px 0', fontWeight: 'bold' }}>
            Unresolved Issues ({unresolvedIssues.length})
          </p>

          {unresolvedIssues.map((issue) => {
            const isSelected = issue.objectId === selectedIssueId

            return (
              <div
                key={issue.objectId}
                style={{
                  border: '1px solid var(--border-default)',
                  borderRadius: '8px',
                  marginBottom: '12px',
                  backgroundColor: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  overflow: 'hidden',
                }}
              >
                {/* Issue Header - Click to expand */}
                <div
                  onClick={() => setSelectedIssueId(issue.objectId)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <div style={{ fontSize: '18px' }}>
                    {isSelected ? '▼' : '▶'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: 'bold', color: 'var(--status-error-text)' }}>
                      {issue.issue || 'No description'}
                    </p>
                  </div>
                </div>

                {/* Expanded Details - Only when selected */}
                {isSelected && (
                  <div style={{ padding: '0 16px 16px 16px', borderTop: '1px solid var(--border-default)' }}>
                    <p style={{ margin: '12px 0 4px 0', fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                      <strong>Reported:</strong> {formatDate(new Date(issue.statusDate))}
                    </p>
                    <p style={{ margin: '4px 0', fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                      <strong>Reported by:</strong> {issue.reportedBy || DEFAULT_USER}
                    </p>
                    <p style={{ margin: '4px 0 12px 0', fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                      <strong>Source:</strong> {issue.source || VAN_STATUS_SOURCES.MANUAL}
                    </p>

                    <TextField
                      label='Resolution Note (Required)'
                      value={resolvedNote}
                      onChange={(e) => setResolvedNote(e.target.value)}
                      fullWidth
                      multiline
                      rows={4}
                      margin='normal'
                      required
                      placeholder='Explain how this specific issue was resolved...'
                    />

                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                      <PrimaryButton
                        buttonName={isResolvingIssue ? 'Resolving...' : 'Resolve Issue'}
                        onClick={handleResolveIssue}
                        disabled={isResolvingIssue || !resolvedNote.trim()}
                      />
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Bottom Actions */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
          <SecondaryButton
            buttonName='Skip Issues'
            onClick={() => setMode(VAN_DOWN_MODAL_MODES.CHANGE_STATUS)}
            disabled={isResolvingIssue}
          />
        </div>
      </div>
    )
  }

  function renderChangeStatusMode() {
    const unresolvedIssues = allIssues.filter((i) => !i.resolved)

    return (
      <div>
        {unresolvedIssues.length > 0 && !forceStatusChange && (
          <div
            style={{
              backgroundColor: 'var(--status-warning-bg)',
              border: '2px solid var(--status-warning-accent)',
              padding: '16px',
              borderRadius: '4px',
              marginBottom: '16px',
            }}
          >
            <p style={{ margin: 0, color: 'var(--status-warning-text)', fontWeight: 'bold' }}>
              ⚠️ Warning: {unresolvedIssues.length} unresolved issue(s) remain
            </p>
            <p style={{ margin: '8px 0 0 0', color: 'var(--status-warning-text)' }}>
              If you mark this van as Active or Standby, it will remain in the Van Down queue
              with status "Active with Unresolved Issue" until all issues are resolved.
            </p>
          </div>
        )}

        {unresolvedIssues.length === 0 && (
          <div
            style={{
              backgroundColor: 'var(--status-success-bg)',
              padding: '16px',
              borderRadius: '4px',
              marginBottom: '16px',
            }}
          >
            <p style={{ margin: 0, color: 'var(--status-success-text)', fontWeight: 'bold' }}>
              ✅ All issues have been resolved!
            </p>
            <p style={{ margin: '8px 0 0 0', color: 'var(--status-success-text)' }}>
              You can now mark the van as active or standby.
            </p>
          </div>
        )}

        <TextField
          select
          label='New Operating Status'
          value={operatingStatus}
          onChange={(e) => setOperatingStatus(e.target.value)}
          fullWidth
          margin='normal'
        >
          {OPERATING_STATUSES_ARRAY.map((status) => (
            <MenuItem
              key={status}
              value={status}
            >
              {status}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label='Effective Date'
          type='date'
          value={statusDate}
          onChange={(e) => setStatusDate(e.target.value)}
          fullWidth
          margin='normal'
          InputLabelProps={{ shrink: true }}
        />

        <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
          {unresolvedIssues.length > 0 && (
            <SecondaryButton
              buttonName='← Back to Issues'
              onClick={() => setMode(VAN_DOWN_MODAL_MODES.RESOLVE_ISSUES)}
              disabled={isChangingStatus}
            />
          )}
          <div style={{ flex: 1 }} />
          <PrimaryButton
            buttonName={
              isChangingStatus
                ? 'Updating...'
                : (forceStatusChange
                    ? 'Force Update'
                    : 'Update Status')
            }
            onClick={handleStatusChange}
            disabled={isChangingStatus}
          />
        </div>
      </div>
    )
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
    >
      <Box className={styles.vanDownModal}>
        <div className={styles.header}>
          <h2>
            {mode === VAN_DOWN_MODAL_MODES.RESOLVE_ISSUES
              ? 'Resolve Van Issues'
              : 'Change Van Status'}
          </h2>
          <h3>Van: {vanName}</h3>
        </div>

        <div className={styles.content}>
          <div className={styles.currentInfo}>
            <p>
              <strong>Current Status:</strong> {currentStatus}
            </p>
            <p>
              <strong>InOp Since:</strong>{' '}
              {inOpDate ? formatDate(new Date(inOpDate)) : '-'}
            </p>
          </div>

          {mode === VAN_DOWN_MODAL_MODES.RESOLVE_ISSUES
            ? renderResolveIssuesMode()
            : renderChangeStatusMode()}
        </div>

        <div className={styles.actions}>
          <SecondaryButton
            buttonName='Cancel'
            onClick={onClose}
            disabled={isResolvingIssue || isChangingStatus}
          />
        </div>
      </Box>
    </Modal>
  )
}
